import React from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Link,
  Button,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  background: "#fff",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    minHeight: "100vh",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    justifyContent: "flex-start",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(1),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(0),
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "30px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  color: "#4b5b5d",
  fontSize: "20px",
  fontWeight: 600,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #f5f0e7 0%, #f8f9fa 100%)",
  border: "1px solid #e9ecef",
  borderRadius: "12px",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
}));

const RequirementList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  backgroundColor: "#f8f9fa",
  borderRadius: "6px",
  border: "1px solid #e9ecef",
}));

const RequirementItem = styled(Typography)(({ theme }) => ({
  fontSize: "0.9rem",
  color: "#4b5b5d",
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  "&::before": {
    content: '"•"',
    marginRight: theme.spacing(1),
    color: "#4b5b5d",
    fontWeight: "bold",
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #5E7273, #455354)",
  color: "#FFFFFF",
  borderRadius: "5px",
  padding: theme.spacing(1, 3),
  fontSize: "14px",
  "&:hover": {
    backgroundColor: "#354142",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1, 2),
    fontSize: "13px",
  },
}));

export const ContactUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const requirements = [
    "Site address",
    "Number and type of chargers",
    "Type of location (e.g., restaurant, parking lot, retail center, hotel, etc.)",
  ];

  return (
    <Container>
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <Content>
            <WelcomeText>Contact Us</WelcomeText>

            <Box
              sx={{
                background: "linear-gradient(135deg, #edefee 0%, #fcfbf9 100%)",
                padding: isMobile ? 2 : 2.5,
                borderRadius: 2,
              }}
            >
              {/* Service Description */}
              <InfoCard>
                <CardContent>
                  <SubHeading>DC Fast Charge Estimates</SubHeading>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#4b5b5d",
                      lineHeight: 1.6,
                      fontSize: "0.95rem",
                      mb: 2,
                    }}
                  >
                    Our Level 3 (DC Fast Charge) estimates are manually prepared
                    by our analyst team to ensure the highest level of accuracy.
                    Typical turnaround time is <strong>48–72 hours</strong>.
                  </Typography>
                </CardContent>
              </InfoCard>

              {/* Requirements Section */}
              <InfoCard>
                <CardContent>
                  <SubHeading>Get Started with Your Estimate</SubHeading>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#4b5b5d",
                      lineHeight: 1.6,
                      fontSize: "0.95rem",
                      mb: 2,
                    }}
                  >
                    To begin the process, please provide the following
                    information:
                  </Typography>

                  <RequirementList>
                    {requirements.map((requirement, index) => (
                      <RequirementItem key={index}>
                        {requirement}
                      </RequirementItem>
                    ))}
                  </RequirementList>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "#4b5b5d",
                      lineHeight: 1.6,
                      fontSize: "0.95rem",
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    Once you have these details ready, please send them to:
                  </Typography>

                  <Box sx={{ textAlign: "center", mt: 3 }}>
                    <PrimaryButton
                      variant="contained"
                      startIcon={<EmailIcon />}
                      onClick={() =>
                        (window.location.href = "mailto:James.lightfoot@rightsite.net")
                      }
                    >
                      Send Email
                    </PrimaryButton>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6c757d",
                        mt: 1,
                        fontStyle: "normal",
                      }}
                    >
                      Our team will get started right away.
                    </Typography>
                  </Box>
                </CardContent>
              </InfoCard>
            </Box>
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};
